import type { BalanceMapping } from "@src/components/DefuseSDK/features/machines/depositedBalanceMachine"
import type { TokenBalanceDisplay } from "@src/components/DefuseSDK/types/asset"
import type {
  BaseTokenInfo,
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import {
  isSamePaymentFamily,
  resolveBaseTokenForPayment,
} from "@src/components/DefuseSDK/utils/paymentTokenSelection"
import { selectQuoteInputToken } from "@src/components/DefuseSDK/utils/quoteInputTokenSelection"
import {
  findTokenByAssetId,
  getTokenAid,
  getTokenId,
  isBaseToken,
  isFungibleToken,
  isUnifiedToken,
} from "@src/components/DefuseSDK/utils/token"
import {
  adjustDecimals,
  eachBaseTokenInfo,
} from "@src/components/DefuseSDK/utils/tokenUtils"
import {
  formatValueAmount,
  parseDisplayNumber,
  TOKEN_VALUE_UNAVAILABLE_LABEL,
} from "@src/components/DefuseSDK/utils/tokenValue"
import { LIST_TOKENS } from "@src/constants/tokens"
import { CURATED_EARN_VAULTS } from "./constants"
import { getEarnVaultDisplayName, isEarnVaultToken } from "./earnVaultToken"
import type { VaultConfig, VaultMetadata } from "./types"

export { getEarnVaultUsdUnitSymbol } from "@src/shared/utils/earnVault"

const VAULT_LEFTOVER_DUST_USDC = 0.01
const EARN_PAYMENT_STABLECOIN_AIDS = new Set(["usdc", "usdt", "dai"])

/**
 * Returns the on-chain output token address for an earn vault (for yield.xyz matching).
 * Uses the deployment for the token's origin chain.
 */
export function getEarnVaultOutputTokenAddress(
  tokenList: TokenInfo[],
  defuseAssetId: string
): string | null {
  const token = findTokenByAssetId(tokenList, defuseAssetId)
  if (!token || !isBaseToken(token)) return null

  const deployment = token.deployments.find(
    (d) => "chainName" in d && d.chainName === token.originChainName
  )
  if (!deployment || !isFungibleToken(deployment)) return null

  return deployment.address
}

const NETWORK_LABELS: Record<string, string> = {
  eth: "Ethereum",
  base: "Base",
  arbitrum: "Arbitrum",
  solana: "Solana",
}

/** Human-friendly network label for a vault (e.g. "eth" -> "Ethereum"). */
export function formatVaultNetwork(
  network?: string | null
): string | undefined {
  if (!network) return undefined
  const key = network.toLowerCase()
  return (
    NETWORK_LABELS[key] ?? network.charAt(0).toUpperCase() + network.slice(1)
  )
}

export function findBaseTokenByAssetId(
  tokenList: TokenInfo[],
  defuseAssetId: string
) {
  for (const token of eachBaseTokenInfo(tokenList)) {
    if (token.defuseAssetId === defuseAssetId) return token
  }
  return undefined
}

export function findUnifiedUsdc(tokenList: TokenInfo[]): TokenInfo | undefined {
  return tokenList.find(
    (token) => isUnifiedToken(token) && token.unifiedAssetId === "usdc"
  )
}

function getEarnPaymentAid(token: TokenInfo): string | null {
  return isUnifiedToken(token) ? token.unifiedAssetId : getTokenAid(token)
}

/** Curated stablecoin families that may fund a USDC earn-vault deposit. */
export function isSupportedEarnPaymentToken(
  paymentToken: TokenInfo,
  candidate: TokenInfo
): boolean {
  return (
    isSamePaymentFamily(paymentToken, candidate) ||
    EARN_PAYMENT_STABLECOIN_AIDS.has(getEarnPaymentAid(candidate) ?? "")
  )
}

export function getEarnPaymentTokens(
  paymentToken: TokenInfo,
  allTokens: TokenInfo[] = LIST_TOKENS
): TokenInfo[] {
  const tokensById = new Map<string, TokenInfo>()

  for (const token of [paymentToken, ...allTokens]) {
    const id = getTokenId(token)
    if (tokensById.has(id)) continue
    if (!isSupportedEarnPaymentToken(paymentToken, token)) continue
    tokensById.set(id, token)
  }

  return [...tokensById.values()]
}

/** Tokens required by the dedicated Earn deposit and withdrawal flows. */
export function getEarnTokenList(
  vault: VaultConfig,
  allTokens: TokenInfo[] = LIST_TOKENS
): TokenInfo[] {
  const paymentTokens = getEarnPaymentTokens(vault.paymentToken, allTokens)
  return [vault.vaultToken, ...paymentTokens]
}

/** Adds only curated vault shares to the generic Send token list. */
export function getEarnSendTokenList(
  tokenList: TokenInfo[],
  allTokens: TokenInfo[] = LIST_TOKENS
): TokenInfo[] {
  const existingIds = new Set(tokenList.map(getTokenId))
  const allBaseTokens = [...eachBaseTokenInfo(allTokens)]

  const curatedVaults = CURATED_EARN_VAULTS.flatMap((definition) => {
    const token = allBaseTokens.find(
      (candidate) =>
        candidate.symbol === definition.tokenSymbol &&
        isEarnVaultToken(candidate) &&
        !existingIds.has(candidate.defuseAssetId)
    )
    if (token == null) return []
    existingIds.add(token.defuseAssetId)
    return [token]
  })

  return [...tokenList, ...curatedVaults]
}

/** Vault share tokens are deposit receipts — not valid Pay with sources on deposit. */
export function withoutEarnVaultTokens(tokens: TokenInfo[]): TokenInfo[] {
  return tokens.filter((token) => !isEarnVaultToken(token))
}

/** Display-only vault labels. Does not mutate `token.symbol` / `token.name`. */
export function getEarnVaultDisplayLabels(
  token: TokenInfo
): { displaySymbol: string; displayName: string } | undefined {
  if (!isBaseToken(token) || !isEarnVaultToken(token)) {
    return undefined
  }
  const definition = CURATED_EARN_VAULTS.find(
    (entry) => entry.tokenSymbol === token.symbol
  )
  if (definition == null) return undefined

  return {
    displaySymbol: definition.shortName ?? definition.displayName,
    displayName: definition.description,
  }
}

export function getEarnVaultMinimumDepositUsdc(
  token: TokenInfo
): number | undefined {
  if (!isBaseToken(token) || !isEarnVaultToken(token)) return undefined
  return CURATED_EARN_VAULTS.find((entry) => entry.tokenSymbol === token.symbol)
    ?.minimumDepositUsdc
}

function isNoLiquidityQuoteError(reason: string): boolean {
  const upperReason = reason.toUpperCase()
  return (
    upperReason.includes("NO LIQUIDITY") || upperReason.includes("NO_QUOTES")
  )
}

function formatUsdcMinimum(value: number): string {
  return Number.isInteger(value) ? value.toString() : formatValueAmount(value)
}

/**
 * Temporary client-side workaround for earn vault quotes where the solver
 * reports no liquidity instead of the vault's minimum non-swappable amount.
 *
 * TODO: Remove once infra returns minimum-amount errors for these routes.
 */
export function getEarnVaultMinimumQuoteError(params: {
  reason: string
  token: TokenInfo | null | undefined
  amountUsdc: number | null | undefined
}): string | null {
  if (params.token == null || !isNoLiquidityQuoteError(params.reason)) {
    return null
  }

  const minimumUsdc = getEarnVaultMinimumDepositUsdc(params.token)
  if (minimumUsdc == null) return null

  if (
    params.amountUsdc == null ||
    !Number.isFinite(params.amountUsdc) ||
    params.amountUsdc >= minimumUsdc
  ) {
    return null
  }

  return `Amount is too low. Try at least ${formatUsdcMinimum(minimumUsdc)} USDC.`
}

export function getVaultUnspendableLeftoverWarning(params: {
  token: TokenInfo
  balanceUsdc: number | null | undefined
  spendUsdc: number | null | undefined
  minimumDepositUsdc?: number | null
}): string | null {
  if (!isEarnVaultToken(params.token)) return null

  const minimum =
    params.minimumDepositUsdc ?? getEarnVaultMinimumDepositUsdc(params.token)
  if (minimum == null) return null

  const { balanceUsdc, spendUsdc } = params
  if (
    balanceUsdc == null ||
    spendUsdc == null ||
    !Number.isFinite(balanceUsdc) ||
    !Number.isFinite(spendUsdc)
  ) {
    return null
  }

  const remainingUsdc = balanceUsdc - spendUsdc
  if (remainingUsdc <= VAULT_LEFTOVER_DUST_USDC) return null
  if (remainingUsdc >= minimum) return null

  const vaultName = getEarnVaultDisplayName(params.token)
  return `You'll leave about ${formatValueAmount(remainingUsdc)} USDC in ${vaultName}. Balances below ${minimum} USDC can't be withdrawn.`
}

/**
 * Single source of truth for the submit button's enabled state and label so the
 * two can't drift. Conditions are evaluated in priority order.
 */
export function getEarnSubmitState(params: {
  isLoggedOut: boolean
  amountInEmpty: boolean
  amountOutEmpty: boolean
  isLoadingQuote: boolean
  needsWithdrawPrice: boolean
  balanceInsufficient: boolean
  validationError: string | null
  failedToGetAQuote: boolean
  modeLabel: string
}): { disabled: boolean; label: string } {
  if (params.isLoggedOut) return { disabled: true, label: "Connect wallet" }
  if (params.amountInEmpty) return { disabled: true, label: "Enter an amount" }
  if (params.needsWithdrawPrice)
    return { disabled: true, label: "Loading price" }
  if (params.balanceInsufficient) {
    return { disabled: true, label: "Insufficient balance" }
  }
  if (params.validationError != null) {
    return { disabled: true, label: params.validationError }
  }
  if (params.failedToGetAQuote) {
    return { disabled: true, label: "An error occurred. Please try again" }
  }
  // Amount entered but quote not ready yet — keep disabled, spinner shown via
  // the Button `loading` prop.
  if (params.isLoadingQuote || params.amountOutEmpty) {
    return { disabled: true, label: params.modeLabel }
  }
  return { disabled: false, label: params.modeLabel }
}

export function getEarnMinimumAmountError(params: {
  mode: "deposit" | "withdraw"
  minimumDepositUsdc?: number
  amountIn: string
  amountUsd: number | null
  quotedAmountUsdc?: number | null
  tokenIn: TokenInfo
  paymentToken: TokenInfo | undefined
}): string | null {
  if (params.minimumDepositUsdc == null || params.amountIn === "") {
    return null
  }

  const amountUsdc =
    params.mode === "withdraw" ||
    (params.paymentToken != null &&
      isSamePaymentFamily(params.paymentToken, params.tokenIn))
      ? parseDisplayNumber(params.amountIn)
      : (params.quotedAmountUsdc ?? params.amountUsd)

  return amountUsdc == null || amountUsdc < params.minimumDepositUsdc
    ? `Enter at least ${params.minimumDepositUsdc} USDC`
    : null
}

/** Select-assets display overrides for a vault share. */
export function buildVaultShareBalanceDisplay(params: {
  usdcValue: number | null | undefined
  usdValue?: number | null
  displaySymbol?: string
  displayName?: string
}): TokenBalanceDisplay {
  const { usdcValue, usdValue, displaySymbol, displayName } = params
  const labels = {
    ...(displaySymbol == null ? {} : { displaySymbol }),
    ...(displayName == null ? {} : { displayName }),
  }
  const usdData =
    usdValue != null && Number.isFinite(usdValue) && usdValue >= 0
      ? { usdValue }
      : {}
  if (usdcValue == null || !Number.isFinite(usdcValue) || usdcValue < 0) {
    return {
      ...labels,
      ...usdData,
      valueLabel: TOKEN_VALUE_UNAVAILABLE_LABEL,
      amountLabel: TOKEN_VALUE_UNAVAILABLE_LABEL,
    }
  }

  const valueLabel = `~${formatValueAmount(usdcValue)} USDC`
  return {
    ...labels,
    ...usdData,
    valueLabel,
    amountLabel: valueLabel,
  }
}

/** Below this magnitude of change, the estimate-change dialog shouldn't interrupt the user. */
export const EARN_ESTIMATE_CHANGE_THRESHOLD_PERCENT = 0.1

/**
 * Relative change between two quote amounts, formatted as an unsigned
 * percent for the "maximum fee" alert — doesn't expose the underlying
 * vault-share quantity. Handles increases too, though the caller only ever
 * invokes this on a worse (decreased) quote — the guard that opens the
 * dialog never fires on an improved one.
 */
export function formatEstimateChangePercent(
  previous: TokenValue,
  next: TokenValue
): string {
  const decimals = Math.max(previous.decimals, next.decimals)
  const prev = adjustDecimals(previous.amount, previous.decimals, decimals)
  const curr = adjustDecimals(next.amount, next.decimals, decimals)
  if (prev === 0n) return "0%"

  const pct = (Number(curr - prev) / Number(prev)) * 100
  if (!Number.isFinite(pct) || pct === 0) return "0%"

  const abs = Math.abs(pct)
  if (abs < 0.01) return "<0.01%"
  return `${abs.toFixed(2)}%`
}

/**
 * Resolves which supported base-token variant the swap machine should quote.
 * The UI may show a unified stablecoin; the machine needs a concrete token.
 */
export function resolveEarnDepositTokenIn(
  paymentToken: TokenInfo,
  paymentTokens: readonly TokenInfo[],
  balances: BalanceMapping,
  amount: TokenValue | null,
  currentTokenIn?: TokenInfo
): BaseTokenInfo {
  if (
    currentTokenIn != null &&
    isBaseToken(currentTokenIn) &&
    isSupportedEarnPaymentToken(paymentToken, currentTokenIn) &&
    (balances[currentTokenIn.defuseAssetId] ?? 0n) > 0n
  ) {
    return currentTokenIn
  }

  let preferred = resolveBaseTokenForPayment(paymentToken, balances)

  if (!isBaseToken(paymentToken) && amount != null && amount.amount > 0n) {
    const resolved = selectQuoteInputToken({
      tokenIn: paymentToken,
      parsedAmount: amount,
      balances,
    })
    if (resolved != null) {
      preferred = resolved.tokenIn
    }
  }

  if ((balances[preferred.defuseAssetId] ?? 0n) > 0n) return preferred

  for (const token of paymentTokens) {
    const candidate = resolveBaseTokenForPayment(token, balances)
    if ((balances[candidate.defuseAssetId] ?? 0n) > 0n) return candidate
  }

  return preferred
}

export function formatTvl(value: number): string {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

export function formatFee(
  fee: NonNullable<VaultMetadata["fee"]>
): string | null {
  const parts: string[] = []
  if (fee.performance != null && fee.performance > 0)
    parts.push(`${fee.performance}% perf.`)
  if (fee.management != null && fee.management > 0)
    parts.push(`${fee.management}% mgmt.`)
  if (fee.deposit != null && fee.deposit > 0)
    parts.push(`${fee.deposit}% deposit`)
  if (fee.withdrawal != null && fee.withdrawal > 0)
    parts.push(`${fee.withdrawal}% withdrawal`)
  return parts.length > 0 ? parts.join(", ") : null
}
